// PROTOTYPE: This file was scaffolded quickly and is pending a rewrite. Do not use as a model for other parts of the codebase.
import { useCallback, useRef, useState, type MutableRefObject } from 'react'
import {
    FaMeta,
    FaGoogle,
    FaTiktok,
    FaPinterest,
    FaSnapchat,
} from 'react-icons/fa6'
import { Plus, ArrowLeft } from 'lucide-react'
import { toast } from 'sonner'
import { AD_PLATFORM } from '@storefronts/common-types'
import type { AdPlatform, MetaAd } from '@storefronts/common-types'
import { Button } from '@/components/ui/button'
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription,
    DialogCloseButton,
    DialogFooter,
} from '@/components/ui/dialog'
import AdSelection from '@/components/landingpages/shared/AdSelection'
import type { AdSelectionView } from '@/components/landingpages/shared/adSelectionTypes'
import { AdViewToggle } from '@/components/landingpages/shared/AdViewToggle'
import { StepContent } from '../../shared/StepContent'
import { ManualCreativeUploadForm } from '../shared/ManualCreativeUploadForm'
import {
    useManualCreativeUploadForm,
    type ManualCreativePayload,
} from '../shared/useManualCreativeUploadForm'
import {
    SearchQueryForm,
    buildSearchQueryPayload,
    canSubmitSearchQuery,
    type SearchQueryPayload,
} from '../shared/SearchQueryModal'
import { R } from '@/modules/api/actions'

const ROKT_LOGO_URL =
    'https://res.cloudinary.com/viddyapp/image/upload/v1779295365/ChatGPT_Image_May_20_2026_12_42_28_PM_wdzs0h.png'

function AxonIcon({ size = 18 }: { size?: number }) {
    return (
        <svg
            width={size}
            height={size}
            viewBox="0 0 32 32"
            fill="currentColor"
            xmlns="http://www.w3.org/2000/svg"
        >
            <path d="M15.9787 19.885C14.4817 19.8827 12.9846 20.0021 11.4964 20.2441L6.16162 29.4197C9.25678 27.801 12.619 27.1067 15.9956 27.1091C19.3834 27.1067 22.7448 27.7946 25.8488 29.4253L20.5188 20.2585C19.0234 20.0116 17.5055 19.885 15.9795 19.885H15.9787Z" />
            <path d="M20.5174 20.2578C23.8411 20.8048 27.0549 21.941 30.0154 23.582C32.8652 25.1617 32.3472 28.3529 30.3425 29.6539L30.0033 29.8482C28.9794 30.3625 27.6443 30.4087 26.1985 29.6141C26.0815 29.5496 25.9644 29.4875 25.8473 29.4254L20.5166 20.2578H20.5174Z" />
            <path d="M11.4965 20.2441C8.25217 20.772 5.05037 21.882 1.98408 23.5819C-0.844865 25.1504 -0.35413 28.309 1.61522 29.6259L2.04101 29.8703C3.05856 30.3632 4.37521 30.3966 5.7993 29.614C5.91958 29.5479 6.04066 29.4834 6.16174 29.4197L11.4965 20.2441Z" />
            <path d="M19.1183 7.13621C19.7622 6.02867 19.7405 4.7205 19.1768 3.6687L31.5021 24.8671C31.6449 25.1123 31.7547 25.3679 31.8333 25.6282C31.5711 24.8511 30.9906 24.1226 30.0163 23.5828C27.0559 21.9418 23.8412 20.8056 20.5183 20.2586L16.0039 12.4947L19.1183 7.1378V7.13621Z" />
            <path d="M16.0027 12.4931L12.8883 7.13621C12.2444 6.02867 12.266 4.7205 12.8297 3.6687L0.504421 24.8671C0.388954 25.0653 0.295137 25.2699 0.222168 25.4778C0.506024 24.7572 1.07294 24.0876 1.98384 23.582C5.05013 21.8813 8.25194 20.7713 11.4962 20.2443L16.0027 12.4931Z" />
            <path d="M19.1765 3.66864C19.741 4.72044 19.7627 6.02862 19.118 7.13615L16.0036 12.4931L12.8884 7.13615C12.2445 6.02862 12.2661 4.72044 12.8306 3.66864L12.8908 3.56593C13.5547 2.42257 14.7607 1.78242 16.0012 1.78003C17.2432 1.78003 18.4508 2.42098 19.1164 3.56593L19.1765 3.66864Z" />
        </svg>
    )
}

const CHANNELS: Array<{
    platform: AdPlatform
    label: string
    icon: React.ReactNode
}> = [
    { platform: AD_PLATFORM.META, label: 'Meta', icon: <FaMeta size={20} /> },
    {
        platform: AD_PLATFORM.GOOGLE,
        label: 'Google',
        icon: <FaGoogle size={20} />,
    },
    {
        platform: AD_PLATFORM.TIKTOK,
        label: 'TikTok',
        icon: <FaTiktok size={20} />,
    },
    {
        platform: AD_PLATFORM.AXON,
        label: 'Axon',
        icon: <AxonIcon size={20} />,
    },
    {
        platform: AD_PLATFORM.PINTEREST,
        label: 'Pinterest',
        icon: <FaPinterest size={20} />,
    },
    {
        platform: AD_PLATFORM.SNAPCHAT,
        label: 'Snapchat',
        icon: <FaSnapchat size={20} />,
    },
    {
        platform: AD_PLATFORM.ROKT,
        label: 'Rokt',
        icon: (
            // eslint-disable-next-line @next/next/no-img-element
            <img
                src={ROKT_LOGO_URL}
                alt="Rokt"
                style={{ height: 20, width: 'auto' }}
            />
        ),
    },
    {
        platform: AD_PLATFORM.UNSPECIFIED,
        label: 'Other',
        icon: <Plus size={20} />,
    },
]

type MetaAdsStatus = 'idle' | 'loading' | 'success' | 'error'
type WizardStep = 'channel' | 'meta-ad' | 'manual-creative' | 'search-query'

export interface ChannelCreativeResult {
    channel: AdPlatform
    ad?: MetaAd
    manualCreative?: ManualCreativePayload
    searchQuery?: SearchQueryPayload
}

interface ChannelCreativePickerModalProps {
    open: boolean
    onOpenChange: (open: boolean) => void
    onSelected: (result: ChannelCreativeResult) => void
}

export function ChannelCreativePickerModal({
    open,
    onOpenChange,
    onSelected,
}: ChannelCreativePickerModalProps) {
    // Set by the manual-creative step while uploadAndBuild is in flight. All
    // dismiss paths (overlay click, Escape, X button) route through
    // Root.onOpenChange, so gating here blocks every vector at once — a
    // cancelled dismiss during upload would otherwise still commit the
    // creative via onSelected after the promise resolved.
    const uploadingRef = useRef(false)
    const handleOpenChange = useCallback(
        (next: boolean) => {
            if (!next && uploadingRef.current) return
            onOpenChange(next)
        },
        [onOpenChange],
    )
    return (
        <Dialog open={open} onOpenChange={handleOpenChange}>
            {open && (
                <ChannelCreativePickerContent
                    onOpenChange={onOpenChange}
                    onSelected={onSelected}
                    uploadingRef={uploadingRef}
                />
            )}
        </Dialog>
    )
}

function ChannelCreativePickerContent({
    onOpenChange,
    onSelected,
    uploadingRef,
}: Omit<ChannelCreativePickerModalProps, 'open'> & {
    uploadingRef: MutableRefObject<boolean>
}) {
    const [step, setStep] = useState<WizardStep>('channel')
    // The channel the wizard is currently building against — set the moment a
    // tile is clicked, since that click auto-advances to the creative step.
    const [selectedChannel, setSelectedChannel] = useState<AdPlatform | null>(
        null,
    )
    const [selectedAd, setSelectedAd] = useState<MetaAd | undefined>()
    const [metaAdView, setMetaAdView] = useState<AdSelectionView>('grid')
    const [ads, setAds] = useState<MetaAd[]>([])
    const [adsStatus, setAdsStatus] = useState<MetaAdsStatus>('idle')
    const [adsError, setAdsError] = useState<unknown>(null)

    const manualForm = useManualCreativeUploadForm('paid')
    const {
        canSubmit: manualCanSubmit,
        uploadAndBuild: uploadManualCreative,
        reset: resetManualForm,
    } = manualForm
    const [manualUploading, setManualUploading] = useState(false)
    const [searchQuery, setSearchQuery] = useState('')
    const [currentLandingPageUrl, setCurrentLandingPageUrl] = useState('')

    const close = useCallback(() => onOpenChange(false), [onOpenChange])

    const fetchMetaAds = useCallback(() => {
        setAdsStatus('loading')
        setAdsError(null)
        R.getMetaAds()
            .then((metaAds) => {
                setAds(metaAds)
                setAdsStatus('success')
            })
            .catch((err) => {
                setAdsError(err)
                setAdsStatus('error')
            })
    }, [])

    const handleChannelClick = useCallback(
        (platform: AdPlatform) => {
            setSelectedChannel(platform)
            if (platform === AD_PLATFORM.META) {
                setStep('meta-ad')
                fetchMetaAds()
                return
            }
            if (platform === AD_PLATFORM.GOOGLE) {
                setStep('search-query')
                return
            }
            setStep('manual-creative')
        },
        [fetchMetaAds],
    )

    const handleBackToChannel = useCallback(() => {
        setStep('channel')
        setSelectedChannel(null)
        setSelectedAd(undefined)
        setSearchQuery('')
        setCurrentLandingPageUrl('')
        resetManualForm()
    }, [resetManualForm])

    const handleAdContinue = useCallback(() => {
        if (!selectedAd || !selectedChannel) return
        onSelected({ channel: selectedChannel, ad: selectedAd })
        close()
    }, [selectedAd, selectedChannel, onSelected, close])

    const handleSearchQueryContinue = useCallback(() => {
        if (!selectedChannel) return
        try {
            const payload = buildSearchQueryPayload(
                searchQuery,
                currentLandingPageUrl,
            )
            onSelected({ channel: selectedChannel, searchQuery: payload })
            close()
        } catch (err) {
            toast.error(
                err instanceof Error
                    ? err.message
                    : 'Please enter a search query',
            )
        }
    }, [selectedChannel, searchQuery, currentLandingPageUrl, onSelected, close])

    const handleManualContinue = useCallback(async () => {
        if (!selectedChannel) return
        setManualUploading(true)
        uploadingRef.current = true
        try {
            const payload = await uploadManualCreative()
            onSelected({ channel: selectedChannel, manualCreative: payload })
            close()
        } catch (err) {
            console.error('Manual creative upload error:', err)
            toast.error(
                err instanceof Error
                    ? err.message
                    : 'Failed to upload creative. Please try again.',
            )
        } finally {
            uploadingRef.current = false
            setManualUploading(false)
        }
    }, [selectedChannel, uploadManualCreative, onSelected, close, uploadingRef])

    const dialogClass =
        step === 'meta-ad'
            ? 'flex h-[calc(100vh-0.5rem)] max-h-[calc(100vh-0.5rem)] w-[95vw] max-w-7xl flex-col overflow-hidden'
            : step === 'manual-creative' || step === 'search-query'
              ? 'flex max-h-[90vh] max-w-2xl flex-col overflow-hidden'
              : 'max-w-lg'
    const searchQueryCanSubmit = canSubmitSearchQuery(
        searchQuery,
        currentLandingPageUrl,
    )

    return (
        <DialogContent className={dialogClass}>
            <DialogHeader>
                <DialogCloseButton disabled={manualUploading} />
                <div className="flex items-start justify-between gap-4 pr-8">
                    <div className="space-y-1.5">
                        <DialogTitle>{wizardTitle(step)}</DialogTitle>
                        <DialogDescription>
                            {wizardDescription(step)}
                        </DialogDescription>
                    </div>
                    {step === 'meta-ad' && (
                        <AdViewToggle
                            view={metaAdView}
                            onViewChange={setMetaAdView}
                        />
                    )}
                </div>
            </DialogHeader>

            {step === 'channel' && (
                <div className="grid grid-cols-4 gap-3 py-4">
                    {CHANNELS.map(({ platform, label, icon }) => (
                        <Button
                            key={platform}
                            variant="outline"
                            className="flex h-16 flex-col items-center gap-1.5 rounded-xl"
                            onClick={() => handleChannelClick(platform)}
                        >
                            {icon}
                            <span className="text-xs font-medium">{label}</span>
                        </Button>
                    ))}
                </div>
            )}

            {step === 'meta-ad' && (
                <div className="flex min-h-0 flex-1 flex-col overflow-hidden pt-2">
                    <StepContent
                        isLoading={adsStatus === 'loading'}
                        isError={adsStatus === 'error'}
                        loadingMessage="Loading ads from Meta..."
                        errorMessage="Failed to load Meta ads."
                        error={adsError}
                    >
                        {adsStatus === 'success' && (
                            <AdSelection
                                ads={ads}
                                selectedAdId={selectedAd?.id}
                                onAdSelect={setSelectedAd}
                                onContinue={handleAdContinue}
                                boundedHeight
                                view={metaAdView}
                                onViewChange={setMetaAdView}
                            />
                        )}
                    </StepContent>
                </div>
            )}

            {step === 'manual-creative' && (
                <div className="flex min-h-0 flex-1 flex-col overflow-y-auto pt-2">
                    <ManualCreativeUploadForm
                        form={manualForm}
                        variant="paid"
                        disabled={manualUploading}
                    />
                </div>
            )}

            {step === 'search-query' && (
                <div className="flex min-h-0 flex-1 flex-col overflow-y-auto pt-2">
                    <SearchQueryForm
                        searchQuery={searchQuery}
                        currentLandingPageUrl={currentLandingPageUrl}
                        onSearchQueryChange={setSearchQuery}
                        onCurrentLandingPageUrlChange={setCurrentLandingPageUrl}
                    />
                </div>
            )}

            <DialogFooter className="border-t pt-4 sm:justify-between">
                <WizardFooter
                    step={step}
                    selectedAd={selectedAd}
                    manualCanSubmit={manualCanSubmit}
                    manualUploading={manualUploading}
                    searchQueryCanSubmit={searchQueryCanSubmit}
                    onClose={close}
                    onBack={handleBackToChannel}
                    onAdContinue={handleAdContinue}
                    onManualContinue={handleManualContinue}
                    onSearchQueryContinue={handleSearchQueryContinue}
                />
            </DialogFooter>
        </DialogContent>
    )
}

function wizardTitle(step: WizardStep): string {
    switch (step) {
        case 'channel':
            return 'Pick a channel'
        case 'meta-ad':
            return 'Select a Meta ad'
        case 'search-query':
            return 'Add your search query'
        case 'manual-creative':
            return 'Add your creative'
    }
}

function wizardDescription(step: WizardStep): string {
    switch (step) {
        case 'channel':
            return 'Where does your ad live?'
        case 'meta-ad':
            return 'Choose the ad to build a storefront for.'
        case 'search-query':
            return 'The Google Ads search query this storefront should target.'
        case 'manual-creative':
            return 'Upload the creative assets to build from.'
    }
}

function WizardFooter({
    step,
    selectedAd,
    manualCanSubmit,
    manualUploading,
    searchQueryCanSubmit,
    onClose,
    onBack,
    onAdContinue,
    onManualContinue,
    onSearchQueryContinue,
}: {
    step: WizardStep
    selectedAd: MetaAd | undefined
    manualCanSubmit: boolean
    manualUploading: boolean
    searchQueryCanSubmit: boolean
    onClose: () => void
    onBack: () => void
    onAdContinue: () => void
    onManualContinue: () => void
    onSearchQueryContinue: () => void
}) {
    if (step === 'channel') {
        return (
            <Button variant="outline" onClick={onClose}>
                Cancel
            </Button>
        )
    }

    return (
        <>
            <Button
                variant="outline"
                onClick={onBack}
                disabled={manualUploading}
                className="gap-1"
            >
                <ArrowLeft className="size-4" />
                Back
            </Button>
            <WizardContinueButton
                step={step}
                selectedAd={selectedAd}
                manualCanSubmit={manualCanSubmit}
                manualUploading={manualUploading}
                searchQueryCanSubmit={searchQueryCanSubmit}
                onAdContinue={onAdContinue}
                onManualContinue={onManualContinue}
                onSearchQueryContinue={onSearchQueryContinue}
            />
        </>
    )
}

function WizardContinueButton({
    step,
    selectedAd,
    manualCanSubmit,
    manualUploading,
    searchQueryCanSubmit,
    onAdContinue,
    onManualContinue,
    onSearchQueryContinue,
}: {
    step: Exclude<WizardStep, 'channel'>
    selectedAd: MetaAd | undefined
    manualCanSubmit: boolean
    manualUploading: boolean
    searchQueryCanSubmit: boolean
    onAdContinue: () => void
    onManualContinue: () => void
    onSearchQueryContinue: () => void
}) {
    switch (step) {
        case 'meta-ad':
            return (
                <Button disabled={!selectedAd} onClick={onAdContinue}>
                    Select ad
                </Button>
            )
        case 'search-query':
            return (
                <Button
                    disabled={!searchQueryCanSubmit}
                    onClick={onSearchQueryContinue}
                >
                    Select
                </Button>
            )
        case 'manual-creative':
            return (
                <Button
                    disabled={!manualCanSubmit || manualUploading}
                    onClick={onManualContinue}
                >
                    {manualUploading ? 'Uploading...' : 'Select'}
                </Button>
            )
    }
}
